/**
 * Hook para detectar e gerenciar tenant baseado no subdomínio
 * 
 * Em produção:
 * - educapaco.pacodolumiar.ma.gov.br -> tenant "educapaco"
 * - ntme.pacodolumiar.ma.gov.br -> tenant "ntme"
 * 
 * Em desenvolvimento local (com hosts configurado):
 * - educapaco.localhost -> tenant "educapaco"
 * - ntme.localhost -> tenant "ntme"
 */

import { useMemo } from "react";

/**
 * Mapeamento de subdomínios para tenants
 */
const SUBDOMAIN_TO_TENANT_MAP = {
  educapaco: "educapaco",
  ntme: "ntme",
  localhost: "educapaco", // padrão para desenvolvimento sem subdomínio
};

/**
 * Extrai o subdomínio da URL atual
 */
const getSubdomain = () => {
  if (typeof window === "undefined") return null;

  const hostname = window.location.hostname;
  
  // Remove porta se existir
  const hostnameWithoutPort = hostname.split(":")[0];
  
  // Divide o hostname em partes
  const parts = hostnameWithoutPort.split(".");
  
  // Se for localhost com subdomínio (ex: educapaco.localhost)
  if (parts.length >= 2 && parts[parts.length - 1] === "localhost") {
    return parts[0];
  }
  
  // Se for 127.0.0.1, não tem subdomínio válido
  if (hostnameWithoutPort === "127.0.0.1" || hostnameWithoutPort === "localhost") {
    return null;
  }
  
  // Para domínios reais (ex: educapaco.pacodolumiar.ma.gov.br)
  // Se tiver mais de 2 partes, o primeiro é o subdomínio
  if (parts.length > 2) {
    return parts[0];
  }
  
  // Se não tiver subdomínio claro, retorna null
  return null;
};

/**
 * Hook para obter o tenant atual baseado no subdomínio
 * Retorna null se o subdomínio não for válido (sem fallback padrão)
 */
export const useTenant = () => {
  const { tenant, subdomain, isValid } = useMemo(() => {
    const detectedSubdomain = getSubdomain();
    
    // Se não detectou subdomínio, retorna null (sem tenant válido)
    if (!detectedSubdomain) {
      return {
        tenant: null,
        subdomain: null,
        isValid: false,
      };
    }
    
    const mappedTenant = SUBDOMAIN_TO_TENANT_MAP[detectedSubdomain];
    
    // Se o subdomínio não estiver mapeado, retorna null
    if (!mappedTenant) {
      return {
        tenant: null,
        subdomain: detectedSubdomain,
        isValid: false,
      };
    }
    
    return {
      tenant: mappedTenant,
      subdomain: detectedSubdomain,
      isValid: true,
    };
  }, []);

  return {
    tenant,
    subdomain,
    isValid,
    isEducapaco: tenant === "educapaco",
    isNTME: tenant === "ntme",
  };
};

export default useTenant;
